import { useEffect, useState } from "react";

import { format, parseISO } from "date-fns";
import { IconButton } from "@mui/material";
import { Download, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import { MainLayout } from "@shared/ui/MainLayout";
import MDBox from "@vendor/components/MDBox";
import { useLoading } from "@shared/hooks/useLoading";
import { useUserProfile } from "@shared/hooks/useUserProfile";
import { DataGridTable } from "@shared/ui/DataGridTable";
import { CustomToolbarReloadComponent } from "@shared/ui/MuiTable/CustomToolbarReloadComponent";
import { ErrorServer } from "@shared/ui/alerts";
import { dollarUS } from "@shared/utils/moneyFormat";
import {
    getInvoiceSubmissionList,
    downloadInvoiceSubmissionFile,
    viewInvoiceSubmission,
    cancelInvoiceSubmission,
} from "../api/invoiceApi";
import { subType, types, status } from "../components/CustomChip";

import type { GridColDef } from "@mui/x-data-grid";
import type { InvoiceSubmission } from "../api/invoiceApi";

/** Campos de `userData` (hook legado `useUserProfile`, tipado `unknown`) que esta page lee. */
interface InvoicesVisualizerUserData {
    firstName?: string;
    lastName?: string;
}

export const InvoicesVisualizerPage = () => {
    const { loading, thisLoading, notLoading } = useLoading()
    const { t, i18n: {language} } = useTranslation();
    const [data, setData] = useState<InvoiceSubmission[]>([])
    const navigate = useNavigate();
    const { userData } = useUserProfile();
    const typedUserData = userData as InvoicesVisualizerUserData;

    const getInvoiceSubmissions = () => {
        thisLoading()
        getInvoiceSubmissionList().then((data) => {
            setData(data)
            notLoading()
        }).catch(() => {
            notLoading()
            ErrorServer().then()
        })
    }

    useEffect(() => {
        getInvoiceSubmissions();
    }, []);


    const onView = (row: InvoiceSubmission) => {
        if (row.invoice) {
            navigate(`/gestion-de-facturas/factura/ver/${row.id}`)
        } else {
            thisLoading()
            // Bug preexistente: `row.id` es opcional en el DTO; si viniera undefined,
            // `viewInvoiceSubmission` recibiría `undefined` (comportamiento preexistente,
            // sin guard nuevo).
            viewInvoiceSubmission(row.id!)
                .catch(() => {
                    ErrorServer().then()
                })
                .finally(() => notLoading())
        }
    }

    const onDownload = (row: InvoiceSubmission) => {
        thisLoading()
        downloadInvoiceSubmissionFile(
            row.id!,
            `Factura_Inicial_${typedUserData.firstName}_${typedUserData.lastName}_${format(new Date(), 'dd_MM_yyyy_HH:mm:ss')}.pdf`
        ).finally(() => notLoading())
    }

    const onCancelInvoice = (row: InvoiceSubmission) => {
        const invoiceSelected = row;
        if (invoiceSelected.status === 'Cancelada') {
            Swal.fire({
                title: 'Cancelar factura',
                text: 'Esta factura ya se encuentra cancelada',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (invoiceSelected.status === 'Aprobada') {
            Swal.fire({
                title: 'Cancelar factura',
                text: 'Esta factura ya se encuentra aprobada',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else {
            Swal.fire({
                title: "Cancelar factura",
                text: "¿Deseas cancelar esta factura?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Cancelar",
                cancelButtonText: "Salir",
            }).then((result) => {
                if (result.isConfirmed) {
                    console.log('Cancel invoice');
                    // Bug preexistente: `row.id` es opcional (mismo caso que `onView`/`onDownload`
                    // arriba), y `cancelInvoiceSubmission` resuelve a `string` (no a `{data}`):
                    // el `{ data }` que desestructuraba acá nunca se usaba, así que se quita sin
                    // cambio de comportamiento observable.
                    cancelInvoiceSubmission(invoiceSelected.id!)
                        .then(async () => {
                            Swal.fire({
                                title: 'Cancelación exitosa',
                                text: 'La factura se ha cancelado exitosamente',
                                icon: 'success',
                                confirmButtonText: 'OK'
                            });
                        })
                        .catch((error) => {
                            console.log("error => ", error);
                            Swal.fire({
                                title: 'Cancelar factura',
                                text: 'Error al cancelar factura',
                                icon: 'error',
                                confirmButtonText: 'OK'
                            });
                        })
                        .finally(() => {
                            getInvoiceSubmissions();
                        })
                }
            });
        }
    };

    const columns: GridColDef[] = [
        {
            field: "invoiceNumber",
            headerName: t("reference"),
            flex: 1,
        },
        {
            field: "status",
            headerName: t("status"),
            flex: 1,
            renderCell: (params) => status(params.value, t),
        },
        {
            field: "types",
            headerName: t("type"),
            flex: 1,
            renderCell: (params) => types(params.value, t),
        },
        {
            field: "subTypes",
            headerName: t("subtype"),
            flex: 1,
            renderCell: (params) => subType(params.value, t),
        },
        {
            field: "rejectReason",
            headerName: t("rejectReason"),
            flex: 1,
        },
        {
            field: "sentDate",
            headerName: t("sentInvoiceDate"),
            flex: 1,
            valueFormatter: (value) =>
                value == null ? '' : format(parseISO(value), 'dd/MM/yyyy'),
        },
        {
            field: "total",
            headerName: t("total"),
            flex: 1,
            valueFormatter: (value) => dollarUS.format(value),
        },
        {
            field: "see",
            headerName: t("see"),
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <IconButton onClick={() => onView(params.row)}>
                    <Visibility />
                </IconButton>
            ),
        },
        {
            field: "download",
            headerName: t("download"),
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <IconButton onClick={() => onDownload(params.row)}>
                    <Download />
                </IconButton>
            ),
        },
    ]

    const toolbarActions = (
        <CustomToolbarReloadComponent
            title={t("reloadData")}
            onClickAction={getInvoiceSubmissions}
        />
    );

    return (
        <MainLayout loading={loading}>
            <MDBox py={3}>
                <DataGridTable
                    rows={data}
                    title="Facturas Enviadas"
                    columns={columns}
                    language={language}
                    toolbarActions={toolbarActions}
                />
            </MDBox>
        </MainLayout>
    )
}
